import { Routes, Route, Navigate } from 'react-router-dom';
import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';

import CardSkeleton from 'Components/SkeletonLoader/CardSkeleton';
import LazyErrorBoundary from 'Components/ErrorBoundary/LazyErrorBoundary';

// Lazy-loaded route views — each produces a separate chunk
const JobAssignmentView = React.lazy(() => import('Views/Assignments/JobAssignmentView'));
const MtpStaffProfile = React.lazy(() => import('Views/Staff/MtpStaffProfileView'));
const RepWorkApplication = React.lazy(() => import('Views/WorkApplication/RepWorkApplicationView'));
const ManageRepView = React.lazy(() => import('Views/ManageRep/ManageRepView'));
const NotFound = React.lazy(() => import('Views/NotFound'));

// Helper to check if current time is within a UTC window
const isWithinUtcWindow = (startUtc, endUtc) => {
  if (!startUtc || !endUtc) return false;
  const start = new Date(startUtc.endsWith('Z') ? startUtc : startUtc + 'Z');
  const end = new Date(endUtc.endsWith('Z') ? endUtc : endUtc + 'Z');
  const now = new Date();
  return now.getTime() >= start.getTime() && now.getTime() <= end.getTime();
};

/**
 * TppSwitcher Component
 *
 * Main routing component for the MTP application.
 * Uses React Router v6 with flat routing structure (no nested routes).
 * All routes are defined at the same level within a single Routes component.
 *
 * Route views are lazy-loaded via React.lazy() for code-splitting.
 * A Suspense boundary with CardSkeleton fallback handles the loading state,
 * and LazyErrorBoundary catches chunk load errors with a retry UI.
 *
 * Note: BrowserRouter is provided by index.js - do not add another Router here.
 */
const TppSwitcher = () => {
  const currentUser = useSelector((state) => state.currentUser);
  const settings = useSelector((state) => state.settings);

  // Only show if current UTC time is within the window defined by the dates
  const isAssignmentsRouteVisible = () => {
    const { acceptDeclineStartDate, acceptDeclineEndDate } = settings || {};
    if (!acceptDeclineStartDate || !acceptDeclineEndDate) return false;
    return isWithinUtcWindow(acceptDeclineStartDate, acceptDeclineEndDate);
  };

  const isInterestFormRouteVisible = () => {
    const { applyStartDate, applyEndDate } = settings || {};
    if (!applyStartDate || !applyEndDate) return false;
    return isWithinUtcWindow(applyStartDate, applyEndDate);
  };

  return (
    <LazyErrorBoundary>
      <Suspense fallback={<CardSkeleton count={3} />}>
        <Routes>
          {/* Redirects */}
          <Route path="/" element={<Navigate to="/mtp/profile" replace />} />

          <Route path="/mtp/profile" element={<MtpStaffProfile />} />
          {isInterestFormRouteVisible() && <Route path="/mtp/rep-work-application" element={<RepWorkApplication />} />}

          {isAssignmentsRouteVisible() && <Route path="/mtp/assignments" element={<JobAssignmentView />} />}

          {currentUser?.isManager && settings?.commentsOpen && (
            <Route path="/mtp/manage-rep" element={<ManageRepView />} />
          )}

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </LazyErrorBoundary>
  );
};

export default TppSwitcher;
